import { supabase } from './supabase';
import { withPositions } from './menuOrder';
import type { Product, Category, Option, OptionValue } from './database.types';

/**
 * Couche de SAUVEGARDE de l'éditeur back-office (écritures Supabase).
 * Le resto crée/modifie son menu et réordonne en glisser-déposer → on persiste ici.
 * Erreurs tolérées (log + retour neutre) pour ne jamais casser l'UI.
 * La logique d'ordre pure est dans menuOrder.ts (testée séparément).
 */

function warn(scope: string, error: { message?: string } | null) {
  if (error) console.warn(`[menuEditor] ${scope} échoué:`, error.message);
}

// ----------------------------- Catégories -----------------------------

export async function createCategory(
  tenantId: string,
  fields: Partial<Category> & { name: string; slug: string },
): Promise<Category | null> {
  const { data, error } = await supabase
    .from('categories')
    .insert({ tenant_id: tenantId, position: 0, is_visible: true, ...fields })
    .select()
    .single();
  warn('createCategory', error);
  return (data as Category) ?? null;
}

export async function updateCategory(
  id: string,
  patch: Partial<Category>,
): Promise<Category | null> {
  const { data, error } = await supabase
    .from('categories')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  warn('updateCategory', error);
  return (data as Category) ?? null;
}

/** Supprime une catégorie. Le caller doit vérifier qu'elle ne contient pas de produits. */
export async function deleteCategory(id: string): Promise<void> {
  const { error } = await supabase.from('categories').delete().eq('id', id);
  warn('deleteCategory', error);
}

export async function reorderCategories(orderedIds: string[]): Promise<void> {
  for (const { id, position } of withPositions(orderedIds)) {
    const { error } = await supabase.from('categories').update({ position }).eq('id', id);
    warn('reorderCategories', error);
  }
}

// ------------------------------ Produits ------------------------------

export async function createProduct(
  tenantId: string,
  categoryId: string,
  fields: Partial<Product> & { name: string; slug: string; base_price_cents: number },
): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .insert({
      tenant_id: tenantId,
      category_id: categoryId,
      is_available: true,
      is_featured: false,
      position: 0,
      ...fields,
    })
    .select()
    .single();
  warn('createProduct', error);
  return (data as Product) ?? null;
}

export async function updateProduct(
  id: string,
  patch: Partial<Product>,
): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  warn('updateProduct', error);
  return (data as Product) ?? null;
}

/**
 * Met à jour l'emoji de repli d'un produit (colonne `products.emoji`, ajoutée par SQL).
 * RÉSILIENT : si la colonne n'existe pas encore, on n'échoue pas (log seulement) pour ne
 * jamais casser l'édition produit.
 */
export async function updateProductEmoji(id: string, emoji: string | null): Promise<void> {
  const { error } = await supabase.from('products').update({ emoji }).eq('id', id);
  if (error) console.warn('[menuEditor] emoji non enregistré (migration manquante ?):', error.message);
}

/** Supprime un produit + ses liaisons d'options (product_options). */
export async function deleteProduct(id: string): Promise<void> {
  const l = await supabase.from('product_options').delete().eq('product_id', id);
  warn('deleteProduct.links', l.error);
  const p = await supabase.from('products').delete().eq('id', id);
  warn('deleteProduct', p.error);
}

export async function reorderProducts(orderedIds: string[]): Promise<void> {
  for (const { id, position } of withPositions(orderedIds)) {
    const { error } = await supabase.from('products').update({ position }).eq('id', id);
    warn('reorderProducts', error);
  }
}

/**
 * Déplace un produit dans une autre catégorie et réécrit l'ordre de la catégorie cible
 * (orderedIdsInTarget = ids de la cible APRÈS déplacement, calculés via menuOrder.moveAcross).
 */
export async function moveProductToCategory(
  productId: string,
  toCategoryId: string,
  orderedIdsInTarget: string[],
): Promise<void> {
  const { error } = await supabase
    .from('products')
    .update({ category_id: toCategoryId })
    .eq('id', productId);
  warn('moveProductToCategory', error);
  for (const { id, position } of withPositions(orderedIdsInTarget)) {
    const { error: e } = await supabase.from('products').update({ position }).eq('id', id);
    warn('moveProductToCategory.position', e);
  }
}

// ------------------------- Options & valeurs --------------------------

export async function createOption(
  tenantId: string,
  fields: Partial<Option> & { name: string; slug: string },
): Promise<Option | null> {
  const { data, error } = await supabase
    .from('options')
    .insert({
      tenant_id: tenantId,
      selection: 'single',
      is_required: false,
      min_choices: 0,
      max_choices: 1,
      position: 0,
      pricing_mode: 'flat',
      extra_each_cents: 0,
      ...fields,
    })
    .select()
    .single();
  warn('createOption', error);
  return (data as Option) ?? null;
}

export async function updateOption(
  id: string,
  patch: Partial<Option>,
): Promise<Option | null> {
  const { data, error } = await supabase
    .from('options')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  warn('updateOption', error);
  return (data as Option) ?? null;
}

/** Supprime un groupe d'options et nettoie ses valeurs + ses liaisons produits (cascade propre). */
export async function deleteOption(optionId: string): Promise<void> {
  const v = await supabase.from('option_values').delete().eq('option_id', optionId);
  warn('deleteOption.values', v.error);
  const l = await supabase.from('product_options').delete().eq('option_id', optionId);
  warn('deleteOption.links', l.error);
  const o = await supabase.from('options').delete().eq('id', optionId);
  warn('deleteOption', o.error);
}

export async function addOptionValue(
  optionId: string,
  fields: Partial<OptionValue> & { name: string },
): Promise<OptionValue | null> {
  const { data, error } = await supabase
    .from('option_values')
    .insert({
      option_id: optionId,
      price_delta_cents: 0,
      is_default: false,
      is_available: true,
      position: 0,
      ...fields,
    })
    .select()
    .single();
  warn('addOptionValue', error);
  return (data as OptionValue) ?? null;
}

export async function updateOptionValue(
  id: string,
  patch: Partial<OptionValue>,
): Promise<OptionValue | null> {
  const { data, error } = await supabase
    .from('option_values')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  warn('updateOptionValue', error);
  return (data as OptionValue) ?? null;
}

export async function deleteOptionValue(id: string): Promise<void> {
  const { error } = await supabase.from('option_values').delete().eq('id', id);
  warn('deleteOptionValue', error);
}

export async function reorderOptionValues(orderedIds: string[]): Promise<void> {
  for (const { id, position } of withPositions(orderedIds)) {
    const { error } = await supabase.from('option_values').update({ position }).eq('id', id);
    warn('reorderOptionValues', error);
  }
}

/** Attache un groupe d'options à un produit (bibliothèque réutilisable), à une position. */
export async function linkOptionToProduct(
  productId: string,
  optionId: string,
  position: number,
): Promise<void> {
  const { error } = await supabase
    .from('product_options')
    .insert({ product_id: productId, option_id: optionId, position });
  warn('linkOptionToProduct', error);
}

export async function unlinkProductOption(linkId: string): Promise<void> {
  const { error } = await supabase.from('product_options').delete().eq('id', linkId);
  warn('unlinkProductOption', error);
}

/** Réordonne les groupes d'options d'un produit (positions des liaisons product_options). */
export async function reorderProductOptions(orderedLinkIds: string[]): Promise<void> {
  for (const { id, position } of withPositions(orderedLinkIds)) {
    const { error } = await supabase.from('product_options').update({ position }).eq('id', id);
    warn('reorderProductOptions', error);
  }
}
